import { Component, Output, EventEmitter, Input, OnInit, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { NgSelectModule } from '@ng-select/ng-select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PreventLeadingSpaceDirective } from '@app/shared/components/utils/prevent-leading-space.directive';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { ApiService } from '@app/core/services/API/api-service';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { CustomDateAdapter } from '../add-people-form/add-people-form';
import { Spinner } from '@app/shared/components/spinner/spinner'; // Custom spinner component


export const CUSTOM_DATE_FORMATS = {
  parse: {
    dateInput: 'MM/DD/YYYY',
  },
  display: {
    dateInput: 'MMMM D, YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'MMMM D, YYYY',
    monthYearA11yLabel: 'MMM Y',
  },
};
export interface Person {
  _id: string;
  name: string;
  email: string;
  phone: string;
  dateOfBirth?: Date | null;
  jobInfo: string;
  company?: string;
  jobRoleID?: string;
  timezone: string;
  homeTeamID: string;
  managerID?: string;
  accesslevel: any; // Can be string ID or full object
  utype: string;
  address: string;
  isActive: boolean;
  status: string;
  description?: string;
}

@Component({
  selector: 'app-edit-people-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCheckboxModule,
    NgSelectModule,
    MatProgressSpinnerModule,
    PreventLeadingSpaceDirective,
    MatDatepickerModule,
    MatNativeDateModule,
    Spinner
  ],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' }
  ],
  templateUrl: './edit-people-form.html',
  styleUrl: './edit-people-form.scss'
})
export class EditPeopleForm implements OnInit {
  @Output() personUpdated = new EventEmitter<Person>();
  @Output() close = new EventEmitter<void>();
  @Input() person: Person | null = null;

  // Loading state
  isLoading = false;

  // Form Controls for validation
  personNameControl = new FormControl('', [Validators.required, Validators.minLength(2)]);
  emailControl = new FormControl('', [Validators.required, Validators.email]);
  phoneControl = new FormControl('');
  dobControl = new FormControl<Date | null>(null);
  companyControl = new FormControl<string | null>(null);
  jobTitleControl = new FormControl('');
  jobRoleControl = new FormControl<string | null>(null);
  timezoneControl = new FormControl<string | null>(null);
  homeTeamControl = new FormControl<string | null>(null);
  managerControl = new FormControl<string | null>(null);
  accessLevelControl = new FormControl<string | null>(null);
  addressControl = new FormControl('');
  isActiveControl = new FormControl(true);
  descriptionControl = new FormControl('');

  // Data signals
  jobRoles = signal<any[]>([]);
  homeTeams = signal<any[]>([]);
  managers = signal<any[]>([]);
  companies = signal<any[]>([]);

  // Static options
  timezoneOptions: any[] = [];
  accessLevelOptions: any[] = [];

  // Services
  private apiService = inject(ApiService);
  private toastr = inject(ToastrService);

  ngOnInit(): void {
    // Load form data immediately if person exists
    if (this.person) {
      this.loadPersonData();
    }
    
    // Load dropdown data in background
    this.loadJobRoles();
    this.loadTeams();
    this.loadManagers();
    this.loadAccessLevels();
    this.loadTimezones();
    this.loadCompanies();
  }

  private loadPersonData(): void {
    if (this.person) {
      // Update form controls with person data
      this.personNameControl.setValue(this.person.name);
      this.emailControl.setValue(this.person.email);
      this.phoneControl.setValue(this.person.phone || '');
       if (this.person.dateOfBirth) {
        const dobDate = typeof this.person.dateOfBirth === 'string' 
          ? new Date(this.person.dateOfBirth) 
          : this.person.dateOfBirth;
        this.dobControl.setValue(dobDate);
      } else {
        this.dobControl.setValue(null);
      }
      this.companyControl.setValue(this.person.company || null);
      this.jobTitleControl.setValue(this.person.jobInfo || '');
      this.jobRoleControl.setValue(this.person.jobRoleID || null);
      this.timezoneControl.setValue(this.person.timezone || null);
      this.homeTeamControl.setValue(this.person.homeTeamID || null);
      this.managerControl.setValue(this.person.managerID || null);
      
      // Handle access level - it can be an object or string ID
      const accessLevelId = typeof this.person.accesslevel === 'string' 
        ? this.person.accesslevel 
        : this.person.accesslevel?._id;
      this.accessLevelControl.setValue(accessLevelId || null);
      
      this.addressControl.setValue(this.person.address || '');
      this.isActiveControl.setValue(this.person.isActive !== false);
      this.descriptionControl.setValue(this.person.description || '');
    }
  }

  onClose(): void {
    this.close.emit();
  }

  toggleActiveStatus(): void {
    this.isActiveControl.setValue(!this.isActiveControl.value);
  }

  isFormValid(): boolean {
    return this.personNameControl.valid && this.emailControl.valid;
  }

  async onSubmit(): Promise<void> {
    if (!this.isFormValid() || this.isLoading || !this.person) return;

    this.isLoading = true;

    try {
      const updatedPerson: Person = {
        ...this.person,
        name: this.personNameControl.value || '',
        email: this.emailControl.value || '',
        phone: this.phoneControl.value || '',
        dateOfBirth: this.dobControl.value,
        jobInfo: this.jobTitleControl.value || '',
        company: this.companyControl.value || '',
        jobRoleID: this.jobRoleControl.value || '',
        timezone: this.timezoneControl.value || '',
        homeTeamID: this.homeTeamControl.value || '',
        managerID: this.managerControl.value || '',
        accesslevel: this.accessLevelControl.value || '',
        address: this.addressControl.value || '',
        isActive: this.isActiveControl.value || false,
        description: this.descriptionControl.value || ''
      };

      // Call API to update person
      const result = await firstValueFrom(this.apiService.updatePeople(updatedPerson));
       // Update personData with the response data if available
       const finalUpdatedPerson = {
        ...updatedPerson,
        _id: result.data?._id || updatedPerson._id,
        updatedAt: result.data?.updatedAt || new Date().toISOString()
      };
      
      this.personUpdated.emit(finalUpdatedPerson);
      this.toastr.success(result.message || 'Person updated successfully!');
      this.onClose();
    } catch (error: any) {
      console.error('Error updating person:', error);
      this.toastr.error(error.message || 'Failed to update person. Please try again.');
    } finally {
      this.isLoading = false;
    }
  }

  private loadJobRoles(): void {
    this.apiService.getJobRole().subscribe({
      next: (res: any) => {
        const jobRoles = res.data || res;
        this.jobRoles.set(Array.isArray(jobRoles) ? jobRoles : []);
      },
      error: (error) => {
        console.error('Error loading job roles:', error);
        this.jobRoles.set([]);
      }
    });
  }

  private loadTeams(): void {
    this.apiService.getHomeTeamList().subscribe({
      next: (res: any) => {
        const teams = res.data || res;
        this.homeTeams.set(Array.isArray(teams) ? teams : []);
      },
      error: (error) => {
        console.error('Error loading teams:', error);
        this.homeTeams.set([]);
      }
    });
  }

  private loadAccessLevels(): void {
    this.apiService.getAccessLevelList().subscribe({
      next: (res: any) => {
        const accessLevels = res.data || res;
        this.accessLevelOptions = Array.isArray(accessLevels) ? accessLevels : [];
      },
      error: (error) => {
        console.error('Error loading access levels:', error);
        this.accessLevelOptions = [];
      }
    });
  }
  
  private loadTimezones(): void {
    this.apiService.getTimeZoneList().subscribe({
      next: (res: any) => {
        const timezones = res.data || res;
        this.timezoneOptions = Array.isArray(timezones) ? timezones : [];
      },
      error: (error) => { 
        console.error('Error loading timezones:', error);
        this.timezoneOptions = [];
      }
    });
  }

  private loadManagers(): void {
    this.apiService.getManagerData().subscribe({
      next: (res: any) => {
        const users = res.data || res;
        // Filter users who can be managers (you might want to add a role check here)
        this.managers.set(Array.isArray(users) ? users : []);
      },
      error: (error) => {
        console.error('Error loading managers:', error);
        this.managers.set([]);
      }
    });
  }
  
  private loadCompanies(): void {
    this.apiService.getCompanyList().subscribe({
      next: (res: any) => {
        const companies = res.data || res;
        this.companies.set(Array.isArray(companies) ? companies : []);
      },
      error: (error) => {
        console.error('Error loading companies:', error);
        this.companies.set([]);
      }
    });
  }
}
